import type { Entity, Persisted } from '@tunasong/schemas'
import { Alert, Skeleton, Switch, Typography, useSearchParams, useUserSettings } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { bandHeadings, useNotificationBands } from './notification-bands.js'
import styles from './notification-settings.module.css'

interface BandSectionProps {
  band: Persisted<Entity>
  /** Its name, told apart from a band with the same name (finding 10) */
  heading: string
  /** The deep link named this band: its heading takes focus */
  isTarget: boolean
}

type CalendarSwitch = 'changes' | 'reminders' | 'checkins' | 'cantMake'

/** The switches, in Joni's order (GH #2814 M3b, approved by Brian 2026-10-26); every member sees all four */
const calendarSwitches: { key: CalendarSwitch; label: string; helper?: string }[] = [
  { key: 'changes', label: 'New, moved and cancelled events', helper: 'Changes still show in the calendar.' },
  { key: 'reminders', label: 'Reminders before events' },
  { key: 'checkins', label: 'Check-ins by push and email', helper: 'When off, you’re still asked in the app' },
  { key: 'cantMake', label: 'When someone can’t make it', helper: 'Only for events you organize' },
]

/** One band's calendar notifications (GH #2814 M2 and M3b, spec N4). Each switch is on until turned off. */
const BandSection: FC<BandSectionProps> = props => {
  const { band, heading, isTarget } = props
  const headingId = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  /** A save the server refused (Joni's review of #2936): the switch shows the stored value again, and says so */
  const [isSaveFailed, setSaveFailed] = useState(false)
  const { userSettings, setUserSettings } = useUserSettings(band, { onSaveError: () => setSaveFailed(true) })
  const calendar = userSettings?.calendarNotifications

  useEffect(() => {
    if (!isTarget) {
      return
    }
    headingRef.current?.scrollIntoView?.({ block: 'start' })
    headingRef.current?.focus()
  }, [isTarget])

  const handleSwitch = (key: CalendarSwitch) => async (checked: boolean) => {
    setSaveFailed(false)
    try {
      await setUserSettings({ calendarNotifications: { ...calendar, [key]: checked } })
    } catch {
      setSaveFailed(true)
    }
  }

  return (
    <section className={styles.band} aria-labelledby={headingId}>
      <Typography variant="h6" component="h3" id={headingId} ref={headingRef} tabIndex={-1} className={styles.heading}>
        {heading}
      </Typography>
      {calendarSwitches.map(({ key, label, helper }) => {
        const labelId = `${headingId}-${key}`
        const helperId = helper ? `${headingId}-${key}-helper` : undefined
        const inputId = `${headingId}-${key}-input`
        return (
          <div key={key} className={styles.row}>
            {/* The label and its helper are part of the switch's target (Joni's audit of M1c/M2, finding 7) */}
            <label className={styles.label} htmlFor={inputId}>
              <Typography variant="body2" component="span" id={labelId}>
                {label}
              </Typography>
              {helper ? (
                <Typography variant="caption" component="span" id={helperId} className={styles.helper}>
                  {helper}
                </Typography>
              ) : null}
            </label>
            <Switch
              id={inputId}
              checked={calendar?.[key] !== false}
              onCheckedChange={checked => void handleSwitch(key)(checked)}
              aria-labelledby={labelId}
              aria-describedby={helperId}
            />
          </div>
        )
      })}
      {isSaveFailed ? (
        <Alert severity="error" className={styles.error}>
          Couldn’t save the setting. Try again.
        </Alert>
      ) : null}
    </section>
  )
}

/**
 * The account's Notifications tab (GH #2814 M2, Joni's placement): a "Calendar notifications" section per band the
 * member is in. `?band={id}` moves focus to that band's heading, for the links in e-mails and on the band calendar.
 */
export const NotificationSettings: FC = () => {
  const [searchParams] = useSearchParams()
  const target = searchParams.get('band')
  /** Every band whose calendar notices the server reads a setting for, however deep, and the linked one (GH #2970) */
  const { bands, loading } = useNotificationBands(target)
  const headings = bandHeadings(bands)

  return (
    <div className={styles.container}>
      <Typography variant="h5" component="h2">
        Calendar notifications
      </Typography>
      {/* Once for every band, not in each (Joni's audit of M1c/M2, finding 10); "email" as the UI spells it (Brian) */}
      <Typography variant="caption" component="p" className={styles.helper}>
        Push and email.
      </Typography>
      {loading && bands.length === 0 ? <Skeleton variant="rectangular" height={96} /> : null}
      {!loading && bands.length === 0 ? (
        <Typography variant="body2">Calendar notifications show here for each band you’re in.</Typography>
      ) : null}
      {bands.map(band => (
        <BandSection key={band.id} band={band} heading={headings[band.id] ?? band.id} isTarget={band.id === target} />
      ))}
    </div>
  )
}

export default NotificationSettings
